import formStyles from '../formStyles.module.scss';

/**
 * Generic input field with a label, defaults to a text input, but it can be other types of inputs
 *
 * @param props - The component props
 * @param props.id - string for input id.
 * @param props.name - string for input name.
 * @param props.title - string for title label text.
 * @param props.type - string for input type. (default: 'text')
 * @param props.placeholder - string for input placeholder text. (default: '')
 * @param props.onBlur - onBlur function to be passed to component for form control. onBlur fires every time the input loses focus. example of useage is to calculate a decimal once they finish typing and leave the box
 * @param props.required - boolean for requiring the field on submit. (default: false)
 * @param props.onChange - onChange function to be passed to component for form control.
 * @param props.disabled - boolean attribute that specifies if the input element should be disabled.
 * @param props.value - value of the input.
 * @param props.autoComplete - string for input autoComplete attribute. (default: 'off')
 * @returns The rendered TextInput component.
 *
 * @example
 * ```tsx
 * <TextInput
 *    onChange={(e) => setEmail(e.target.value)}
 *    id="email"
 *    name="email"
 *    title="Email"
 *    type="email"
 *    placeholder="Enter your email address"
 *    required={true}
 *    autoComplete="email"
 * />
 * ```
 */
const TextInput = ({
    id,
    name,
    title,
    type = 'text',
    placeholder = '',
    onBlur,
    required = false,
    onChange,
    disabled,
    value,
    autoComplete = 'off',
}: {
    id: string;
    name: string;
    title: string;
    type?: string;
    placeholder?: string;
    required?: boolean;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onBlur?: React.ChangeEventHandler<HTMLInputElement>;
    disabled?: boolean;
    value?: string | number;
    autoComplete?: string;
}) => {
    return (
        <div data-testid="textinput" className={formStyles.inputcontainer}>
            <label
                data-testid="textinputlabel"
                htmlFor={id}
                className={`${formStyles.label} ${formStyles[id]}`}
            >
                {title}
            </label>
            <input
                value={value}
                onBlur={onBlur}
                onChange={onChange && onChange}
                data-testid="textinputinput"
                type={type}
                id={id}
                name={name}
                placeholder={placeholder}
                required={required}
                className={formStyles.input}
                disabled={disabled}
                autoComplete={autoComplete}
            />
        </div>
    );
};

export default TextInput;
